import { apiMutate } from "~/utils/apiClient";

const { VITE_CONVERSATIONS_API, VITE_ANALYSIS_AGENT_API } = import.meta.env;
export const AGENTS_API_PREFIX = `${VITE_CONVERSATIONS_API}/agents`;
export const DEFAULT_NEW_AGENT_DISPLAY_NAME = "New Agent";

export const agentListPath = () => `${AGENTS_API_PREFIX}`;
export const agentPath = (id: string) => `${AGENTS_API_PREFIX}/${id}`;
export const agentDuplicatePath = (sourceId: string) =>
  `${AGENTS_API_PREFIX}/${sourceId}/duplicate`;
export const toolkitsPath = () => `${VITE_CONVERSATIONS_API}/toolkits`;
export const agentToolkitsPath = (agentId: string) => `${agentPath(agentId)}/toolkits`;
/** Live tools surfaced by a toolkit — served by the agent-runtime (introspected
 * from the toolkit's pydantic-ai toolset, including MCP servers). */
export const toolkitToolsPath = (toolkitId: string) =>
  `${VITE_ANALYSIS_AGENT_API}/toolkits/${toolkitId}/tools`;
/** Live tools surfaced by a connector's external MCP server — served by the
 * agent-runtime. */
export const connectorToolsPath = (connectorId: string) =>
  `${VITE_ANALYSIS_AGENT_API}/connectors/${connectorId}/tools`;
export const agentToolkitPath = (agentId: string, toolkitId: string) =>
  `${agentToolkitsPath(agentId)}/${toolkitId}`;
export const connectorsPath = () => `${VITE_CONVERSATIONS_API}/connectors`;
export const agentConnectorsPath = (agentId: string) => `${agentPath(agentId)}/connectors`;
export const agentConnectorPath = (agentId: string, connectorId: string) =>
  `${agentConnectorsPath(agentId)}/${connectorId}`;
export const agentSharesPath = (agentId: string) => `${agentPath(agentId)}/shares`;

export const AGENT_BRAND_COLORS: string[] = [
  "#685BC7", // manifold
  "#201547", // deep_space
  "#C5B4E3", // moonglow
  "#FF6A39", // forge_fire
  "#2D68C4", // hot_blue
  "#5A5B5D", // graphite
];

export interface Toolkit {
  id: string;
  display_name: string;
  description?: string;
}

/** A single tool surfaced by a toolkit or connector, with its live description. */
export interface ToolInfo {
  name: string;
  description?: string;
}

export interface AgentToolkit {
  toolkit: Toolkit;
  created_at: string;
  created_by: string;
}

export const ConnectorSource = {
  SYSTEM: "SYSTEM",
  USER: "USER",
} as const;
export type ConnectorSource = (typeof ConnectorSource)[keyof typeof ConnectorSource];

export interface Connector {
  id: string;
  source: ConnectorSource;
  name: string;
  /** Human-readable label for the UI. Nullable; callers fall back to `name`
   * (the wire-safe dotted identifier) when this is absent. */
  display_name: string | null;
  description?: string;
  url: string;
}

export interface AgentConnector {
  connector: Connector;
  created_at: string;
  created_by: string | null;
}

export const AgentStatus = {
  DISABLED: "DISABLED",
  ENABLED: "ENABLED",
  DELETED: "DELETED",
} as const;
export type AgentStatus = (typeof AgentStatus)[keyof typeof AgentStatus];

export const AgentScope = {
  SYSTEM: "SYSTEM",
  CUSTOMER: "CUSTOMER",
} as const;
export type AgentScope = (typeof AgentScope)[keyof typeof AgentScope];

/** The caller's own resolved access to an agent, and the level a share confers
 * (owner > editor > user). The wire speaks only this level; clients derive
 * per-action capability from it plus `scope` (see the `useCan*Agent` hooks). */
export const AgentAccessLevel = {
  OWNER: "owner",
  EDITOR: "editor",
  USER: "user",
} as const;
export type AgentAccessLevel = (typeof AgentAccessLevel)[keyof typeof AgentAccessLevel];

/** A user who inherits agent access through a shared user-tag group. */
export interface AgentCollaboratorMember {
  email: string;
  given_name?: string | null;
  family_name?: string | null;
  image_url?: string | null;
}

export interface AgentCollaborator {
  /** Bare prefixed principal: `user:{email}` or `user-tag:{tag}`. */
  principal: string;
  level: AgentAccessLevel;
  /** User enrichment from the listing endpoint; null for group principals. */
  given_name?: string | null;
  family_name?: string | null;
  image_url?: string | null;
  /** Group enrichment from the listing endpoint; absent/empty for users. The
   * share (`POST`) response leaves all enrichment fields unset. */
  display_name?: string | null;
  is_internal?: boolean;
  members?: AgentCollaboratorMember[];
}

export interface AgentCollaboratorList {
  items: AgentCollaborator[];
}

export interface AgentStarterPrompt {
  id: string;
  position: number;
  text: string;
  created_by: string | null;
  created_at: string;
}

export interface AgentFeaturedConversation {
  id: string;
  position: number;
  label: string;
  url: string;
  created_by: string | null;
  created_at: string;
}

export interface AgentStarterPromptInput {
  text: string;
}

export interface AgentFeaturedConversationInput {
  label: string;
  url: string;
}

export interface Agent {
  id: string;
  name: string;
  display_name: string;
  description: string;
  scope: AgentScope;
  customer_id: string | null;
  persona: string;
  instructions: string | null;
  model: string;
  status: AgentStatus;
  parent_agent_id: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
  starter_prompts: AgentStarterPrompt[];
  featured_conversations: AgentFeaturedConversation[];
  /** The caller's own access level; null when they hold no role (e.g. SYSTEM
   * agents, or an admin user who can see all agents without a direct grant).
   * Drives per-action gating (edit, delete, duplicate, share) via the
   * `useCan*Agent` hooks, which derive each capability from this level plus
   * `scope`. */
  access_level: AgentAccessLevel | null;
}

export const MAX_STARTER_PROMPTS = 6;
export const MAX_FEATURED_CONVERSATIONS = 6;
export const STARTER_PROMPT_MAX_LENGTH = 300;
export const FEATURED_CONVERSATION_LABEL_MAX_LENGTH = 100;
export const FEATURED_CONVERSATION_URL_MAX_LENGTH = 2048;

export type PartialAgent = Omit<Agent, "persona">;

export interface AgentListMetadata {
  limit: number;
  offset: number;
  total_count: number;
  has_more: boolean;
}

export interface AgentList {
  items: Agent[];
  metadata: AgentListMetadata;
}

export interface UpdateAgentInput {
  display_name?: string;
  description?: string;
  persona?: string;
  instructions?: string;
  status?: AgentStatus;
  starter_prompts?: AgentStarterPromptInput[];
  featured_conversations?: AgentFeaturedConversationInput[];
}

export async function createAgent(): Promise<Agent> {
  return apiMutate<Agent>(agentListPath(), "POST", {
    display_name: DEFAULT_NEW_AGENT_DISPLAY_NAME,
  });
}

export async function duplicateAgent(sourceId: string): Promise<Agent> {
  return apiMutate<Agent>(agentDuplicatePath(sourceId), "POST");
}

export async function updateAgent(id: string, input: UpdateAgentInput): Promise<Agent> {
  return apiMutate<Agent>(agentPath(id), "PATCH", input);
}

export async function deleteAgent(id: string): Promise<void> {
  await apiMutate(agentPath(id), "DELETE");
}

export async function enableAgentToolkit(agentId: string, toolkitId: string): Promise<void> {
  await apiMutate(agentToolkitsPath(agentId), "POST", { toolkit_id: toolkitId });
}

export async function disableAgentToolkit(agentId: string, toolkitId: string): Promise<void> {
  await apiMutate(agentToolkitPath(agentId, toolkitId), "DELETE", undefined, { expects204: true });
}

export async function enableAgentConnector(agentId: string, connectorId: string): Promise<void> {
  await apiMutate(agentConnectorsPath(agentId), "POST", { connector_id: connectorId });
}

export async function disableAgentConnector(agentId: string, connectorId: string): Promise<void> {
  await apiMutate(agentConnectorPath(agentId, connectorId), "DELETE", undefined, {
    expects204: true,
  });
}

/** Share an agent with a principal at the given level. Idempotent: re-sharing an
 * existing `(principal, level)` succeeds. */
export async function shareAgent(
  agentId: string,
  principal: string,
  level: AgentAccessLevel
): Promise<AgentCollaborator> {
  return apiMutate<AgentCollaborator>(agentSharesPath(agentId), "POST", { principal, level });
}

/** Unshare an agent from a principal. Idempotent: unsharing an absent share
 * still succeeds. */
export async function unshareAgent(
  agentId: string,
  principal: string,
  level: AgentAccessLevel
): Promise<void> {
  await apiMutate(agentSharesPath(agentId), "DELETE", { principal, level }, { expects204: true });
}
